import { isNil, isNotNil } from '@flowio/is';
import postPaymentMethodData from '../../services/paymentRequest/postPaymentMethodData';
import { type DomReference, type PaymentButtons } from '../../types';
import { resolveElement } from '../../utilities/elementHelpers';
import getRollbarInstance from '../../utilities/getRollbarInstance';
import isInternalPaymentRequest from '../../validators/isInternalPaymentRequest';
import {
  APPLEPAY_SUPPORTED_VERSION,
  convertApplePayPaymentInfo,
  getApplePayAvailability,
} from './applePayHelpers';
import injectApplePayCss from './ui-css';
import { renderApplePayUI } from './ui-helpers';

const rollbar = getRollbarInstance();

type HandleApplePayCreateSessionActionParams = {
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  action: io.flow.v0.models.ActionUseSdkApplepayJsCreateSession,
  container: DomReference,
  buttons?: PaymentButtons,
};

function isApplePayCreateSessionAction(
  action: io.flow.v0.unions.Action,
): action is io.flow.v0.models.ActionUseSdkApplepayJsCreateSession {
  return action.type === 'use_sdk_applepay_js_create_session';
}

export default function handleApplePayCreateSessionAction(
  params: HandleApplePayCreateSessionActionParams,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  if (!isApplePayCreateSessionAction(params.action)) {
    throw new Error('handleApplePayCreateSessionAction: incorrect \'action\' parameter type.');
  }

  const availability = getApplePayAvailability();

  if (!availability.available) {
    return Promise.reject(new Error('handleApplePayCreateSessionAction: Client environment does not support Apple Pay.'));
  }

  const resolvedContainer = resolveElement(params.container);

  if (isNil(resolvedContainer)) {
    // eslint-disable-next-line no-console
    console.error('handleKlarnaV1Action: Mounting container was not set.');
    return Promise.reject(new Error('handleKlarnaV1Action: Mounting container was not set.'));
  }

  return new Promise((resolve, reject) => {
    const applePayPaymentHandler = (): void => {
      const session = new ApplePaySession(
        APPLEPAY_SUPPORTED_VERSION,
        convertApplePayPaymentInfo(params.action.payment_data_request),
      );

      session.onvalidatemerchant = (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
        postPaymentMethodData(params.pr, {
          type: 'validate_applepay',
          validation_url: event.validationURL,
        }).then((responsePr) => {
          const responseAction = isInternalPaymentRequest(responsePr)
            ? responsePr.actual_action : responsePr.next_action;

          if (isNotNil(responseAction) && responseAction.type === 'use_sdk_applepay_js_complete_validation') {
            session.completeMerchantValidation(responseAction.payment_session);
            return;
          }
          rollbar.error('beginApplePay: Unexpected response from validate_applepay update.', { actionType: responseAction?.type });
          session.abort();
        }).catch((error) => {
          rollbar.error('applePayPaymentHandler: Error ocurred [validate_applepay].', error);
          reject(new Error('applePayPaymentHandler: Error ocurred [validate_applepay].'));
        });
      };

      session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
        postPaymentMethodData(params.pr, {
          type: 'authorize_applepay',
          result: {
            type: 'success',
            token: JSON.stringify(event.payment.token.paymentData),
          },
        }).then((responsePr) => {
          session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
          resolve(responsePr);
        }).catch((e) => {
          session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
          rollbar.error('applePayPaymentHandler: Error ocurred authorizing via [authorize_applepay].', e);
          reject(new Error('applePayPaymentHandler: Error ocurred authorizing via [authorize_applepay].'));
        });
      };

      session.oncancel = () => {
        postPaymentMethodData(params.pr, {
          type: 'authorize_applepay',
          result: {
            type: 'failure',
            message: 'User cancelled Apple Pay.',
          },
        }).then((responsePr) => {
          resolve(responsePr);
        }).catch(() => {
          rollbar.error('applePayPaymentHandler: Error ocurred cancelling via [authorize_applepay].');
          reject();
        });
      };

      session.begin();
    };

    injectApplePayCss();
    renderApplePayUI(resolvedContainer, params.action, applePayPaymentHandler);
  });
}
